import { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Avatar,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";

/* ---------- ICONS ---------- */
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import GoogleIcon from "@mui/icons-material/Google";
import VerifiedIcon from "@mui/icons-material/Verified";
import LockIcon from "@mui/icons-material/Lock";

import { AuthContext } from "./context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { GoogleLogin } from "@react-oauth/google";
import { BASE_URL } from "./config/api";
import GoelectroniX from "../images/GoelectroniX.png";
import GoelectronixB from "../images/goelectronixB.png";

export default function LoginScreen() {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const [mode, setMode] = useState("otp");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });

  const showToast = (type, msg) =>
    setToast({ open: true, type, msg });

  async function handleSendOtp() {
    if (!email) return showToast("error", "Enter valid email");

    try {
      setBusy(true);
      const res = await axios.post(
        `${BASE_URL}warranty/customer/send-email-otp`,
        { email }
      );

      if (res.data?.status) {
        setOtpSent(true);
        showToast("success", "OTP sent successfully");
      } else {
        showToast("error", "Failed to send OTP");
      }
    } catch {
      showToast("error", "OTP sending failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleVerifyOtp() {
    try {
      setBusy(true);
      const res = await axios.post(
        `${BASE_URL}warranty/customer/verify-email-otp`,
        { email, otp }
      );

      if (res.data?.status) {
        login({ ...res.data.data, role: 7 });
        navigate("/cust", { replace: true });
      } else {
        showToast("error", "Invalid OTP");
      }
    } catch {
      showToast("error", "Verification failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f4f6fb",
        display: "grid",
        placeItems: "center",
        p: 2,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 420 }}>
        <Card
          sx={{
            borderRadius: 4,
            boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Logo */}
            <Box sx={{
                display: "flex",
                justifyContent: "center",
                mb: 2,          // ✅ remove bottom margin
                lineHeight: 0,  // ✅ prevents image inline gap
              }}>
              <Box
                component="img"
                src={GoelectronixB}
                alt="GoelectroniX Logo"
                sx={{
                  width: 200,          // optional: slightly smaller like screenshot
                  height: "auto",
                  display: "block",    // ✅ removes baseline gap
                }}
              />
            </Box>

            {/* Header */}
            <Stack spacing={1} alignItems="center">
              <Avatar
                sx={{
                  bgcolor: "#4f46e5",
                  width: 44,
                  height: 44,
                }}
              >
                <LockIcon />
              </Avatar>

              <Typography variant="h5" fontWeight={700}>
                Warranty Care
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Sign in to manage warranties & claims
              </Typography>
            </Stack>

            {/* Mode Switch */}
            <Stack direction="row" spacing={1} mt={3}>
              <Button
                fullWidth
                variant={mode === "otp" ? "contained" : "outlined"}
                onClick={() => setMode("otp")}
                startIcon={<MailOutlineIcon />}
              >
                Email OTP
              </Button>
              <Button
                fullWidth
                variant={mode === "gmail" ? "contained" : "outlined"}
                onClick={() => setMode("gmail")}
                startIcon={<GoogleIcon />}
              >
                Gmail
              </Button>
            </Stack>

            {/* Login Form */}
            <Box mt={3}>
              {mode === "gmail" ? (
                <GoogleLogin
                  onSuccess={(res) => console.log(res)}
                  onError={() => showToast("error", "Google login failed")}
                  width="100%"
                />
              ) : (
                <Stack spacing={2}>
                  <TextField
                    label="Email"
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />

                  {otpSent && (
                    <TextField
                      label="OTP"
                      fullWidth
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                    />
                  )}

                  {!otpSent ? (
                    <Button
                      fullWidth
                      variant="contained"
                      disabled={busy}
                      onClick={handleSendOtp}
                      startIcon={
                        busy ? <CircularProgress size={18} /> : <MailOutlineIcon />
                      }
                    >
                      Send OTP
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      variant="contained"
                      disabled={busy}
                      onClick={handleVerifyOtp}
                      startIcon={
                        busy ? <CircularProgress size={18} /> : <VerifiedIcon />
                      }
                    >
                      Verify & Login
                    </Button>
                  )}
                </Stack>
              )}
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
              mt={3}
              display="block"
            >
              By continuing, you agree to our Terms & Privacy Policy
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
      >
        <Alert severity={toast.type} variant="filled">
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}


// ********************************************full working code***************************************************************

// import { useContext, useEffect, useState } from "react";
// import {
//   Box,
//   Button,
//   Card,
//   CardContent,
//   Typography,
//   Stack,
//   Avatar,
//   TextField,
//   Snackbar,
//   Alert,
//   CircularProgress,
// } from "@mui/material";

// /* ---------- MUI ICONS ---------- */
// import MailOutlineIcon from "@mui/icons-material/MailOutline";
// import GoogleIcon from "@mui/icons-material/Google";
// import VerifiedIcon from "@mui/icons-material/Verified";
// import LockIcon from "@mui/icons-material/Lock";
// import { AuthContext } from "./context/AuthContext";
// import { useNavigate } from "react-router-dom";

// import axios from "axios";
// import { GoogleLogin } from "@react-oauth/google";
// import { BASE_URL } from "./config/api";
// import GoelectroniX from "../images/GoelectroniX.png"


// export default function LoginScreen() {
//   const { login,logout  } = useContext(AuthContext);
//   const [mode, setMode] = useState("otp"); // 'otp' | 'gmail'
//   const [email, setEmail] = useState("");
//   const [otp, setOtp] = useState("");
//   const [otpSent, setOtpSent] = useState(false);
//   const [busy, setBusy] = useState(false);
//   const [toast, setToast] = useState({ open: false, type: "success", msg: "" });

//   const [cooldown, setCooldown] = useState(0);
//   const [error, setError] = useState("");

//   useEffect(() => {
//   if (cooldown <= 0) return;
//   const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
//   return () => clearInterval(timer);
// }, [cooldown]);


//   const navigate = useNavigate();

//   const showToast = (type, msg) => setToast({ open: true, type, msg });

//   async function handleSendOtp() {
//   if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email)) {
//     showToast("error", "Enter a valid email");
//     return;
//   }

//   try {
//     setBusy(true);
//     setError("");

//     const res = await axios.post(
//       `${BASE_URL}warranty/customer/send-email-otp`,
//       { email }
//     );

//     if (res.data?.status) {
//       setOtpSent(true);
//       setCooldown(120);
//       showToast("success", "OTP sent to your email");
//     } else {
//       showToast("error", res.data?.message || "Failed to send OTP");
//     }
//   } catch (err) {
//     showToast("error", err?.response?.data?.message || "Failed to send OTP");
//   } finally {
//     setBusy(false);
//   }
// }


// async function handleVerifyOtp() {
//   if (!otp || otp.length < 4) {
//     showToast("error", "Enter valid OTP");
//     return;
//   }

//   try {
//     setBusy(true);
//     setError("");

//     const res = await axios.post(
//       `${BASE_URL}warranty/customer/verify-email-otp`,
//       { email, otp }
//     );

//     if (res.data?.status) {
//       login({ ...res.data.data, role: 7 });
//       navigate("/cust", { replace: true });
//     } else {
//       showToast("error", res.data?.message || "Invalid OTP");
//     }
//   } catch (err) {
//     showToast("error", err?.response?.data?.message || "OTP verification failed");
//   } finally {
//     setBusy(false);
//   }
// }


//   const handleGoogleLogin = async (credentialResponse) => {
//   try {
//     setBusy(true);
//     setError("");

//     const token = credentialResponse?.credential;
//     if (!token) {
//       showToast("error", "Google token missing");
//       return;
//     }

//     const res = await axios.post(
//       `${BASE_URL}warranty/customer/auth/google`,
//       { token }
//     );

//     if (res.data?.status) {
//       login({ ...res.data.data, role: 7 });
//       navigate("/cust", { replace: true });
//     } else {
//       showToast("error", res.data?.message || "Google login failed");
//     }
//   } catch (err) {
//     showToast("error", err?.response?.data?.message || "Google login failed");
//   } finally {
//     setBusy(false);
//   }
// };


  

//   return (
//     <Box
//       sx={{
//         minHeight: "100vh",
//         bgcolor: "#0b1220",
//         display: "grid",
//         placeItems: "center",
//         p: 2,
//       }}
//     >
//       <Box sx={{ width: "100%", maxWidth: 440 }}>
//         <Card
//           sx={{
//             borderRadius: 4,
//             overflow: "hidden",
//             background: "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.04))",
//             border: "1px solid rgba(255,255,255,0.12)",
//             color: "#fff",
//           }}
//         >
//           <CardContent sx={{ p: 3 }}>
//             <Box
//               sx={{
//                 display: "flex",
//                 justifyContent: "center",
//                 mb: 2,          // ✅ remove bottom margin
//                 lineHeight: 0,  // ✅ prevents image inline gap
//               }}
//             >
//               <Box
//                 component="img"
//                 src={GoelectroniX}
//                 alt="GoelectroniX Logo"
//                 sx={{
//                   width: 200,          // optional: slightly smaller like screenshot
//                   height: "auto",
//                   display: "block",    // ✅ removes baseline gap
//                 }}
//               />
//             </Box>



//             <Stack spacing={2}>
//               <Stack spacing={1} alignItems="center">
//                 <Avatar
//                   sx={{
//                     bgcolor: "#4f46e5",
//                     width: 44,
//                     height: 44,
//                   }}
//                 >
//                   <LockIcon />
//                 </Avatar>

//                 <Typography variant="h5" fontWeight={700} textAlign="center">
//                   Warranty Care
//                 </Typography>

//                 <Typography
//                   variant="body2"
//                   sx={{ opacity: 0.85, textAlign: "center" }}
//                 >
//                   Sign in to manage devices, claims & warranties
//                 </Typography>
//               </Stack>


//               <Stack direction="row" spacing={1}>
//                 <Button
//                   fullWidth
//                   variant={mode === "otp" ? "contained" : "outlined"}
//                   sx={{ borderRadius: 3 }}
//                   onClick={() => setMode("otp")}
//                   disabled={busy}
//                   startIcon={<MailOutlineIcon />}
//                 >
//                   Email OTP
//                 </Button>
//                 <Button
//                   fullWidth
//                   variant={mode === "gmail" ? "contained" : "outlined"}
//                   sx={{ borderRadius: 3 }}
//                   onClick={() => setMode("gmail")}
//                   disabled={busy}
//                   startIcon={<GoogleIcon />}
//                 >
//                   Gmail
//                 </Button>
//               </Stack>

//               {mode === "gmail" ? (
                
//               <GoogleLogin
//                 onSuccess={handleGoogleLogin}
//                 onError={() => showToast("error", "Google login failed")}
//                 shape="pill"
//               />

//               ) : (
//                 <Card
//                   variant="outlined"
//                   sx={{
//                     borderRadius: 3,
//                     borderColor: "rgba(255,255,255,0.16)",
//                     bgcolor: "rgba(255,255,255,0.04)",
//                   }}
//                 >
//                   <CardContent>
//                     <Typography fontWeight={900}>Email OTP</Typography>
//                     <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
//                       Enter your email and verify OTP to login.
//                     </Typography>

//                     <Stack spacing={1.4} mt={2}>
//                       <TextField
//                         label="Email"
//                         value={email}
//                         onChange={(e) => setEmail(e.target.value)}
//                         fullWidth
//                         InputLabelProps={{ style: { color: "rgba(255,255,255,0.65)" } }}
//                         InputProps={{ style: { color: "#fff" } }}
//                       />

//                       {otpSent ? (
//                         <TextField
//                           label="OTP"
//                           value={otp}
//                           onChange={(e) => setOtp(e.target.value)}
//                           fullWidth
//                           InputLabelProps={{ style: { color: "rgba(255,255,255,0.65)" } }}
//                           InputProps={{ style: { color: "#fff" } }}
//                         />
//                       ) : null}

//                       {!otpSent ? (
//                         <Button
//                           fullWidth
//                           sx={{ borderRadius: 3, py: 1.1 }}
//                           variant="contained"
//                           disabled={busy}
//                           onClick={handleSendOtp}
//                           startIcon={busy ? <CircularProgress size={16} /> : <MailOutlineIcon />}
//                         >
//                           {busy ? "Sending..." : "Send OTP"}
//                         </Button>
//                       ) : (
//                         <Stack direction="row" spacing={1}>
//                           <Button
//                             fullWidth
//                             sx={{ borderRadius: 3, py: 1.1 }}
//                             variant="outlined"
//                             disabled={busy}
//                             onClick={() => {
//                               setOtpSent(false);
//                               setOtp("");
//                             }}
//                           >
//                             Change Email
//                           </Button>
//                           <Button
//                             fullWidth
//                             sx={{ borderRadius: 3, py: 1.1 }}
//                             variant="contained"
//                             disabled={busy}
//                             onClick={handleVerifyOtp}
//                             startIcon={busy ? <CircularProgress size={16} /> : <VerifiedIcon />}
//                           >
//                             {busy ? "Verifying..." : "Verify & Login"}
//                           </Button>
//                         </Stack>
//                       )}
//                     </Stack>
//                   </CardContent>
//                 </Card>
//               )}

//               <Typography variant="caption" sx={{ opacity: 0.75 }}>
//                 By continuing, you agree to Terms & Privacy Policy.
//               </Typography>
//             </Stack>
//           </CardContent>
//         </Card>
//       </Box>

//       <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast((t) => ({ ...t, open: false }))}>
//         <Alert severity={toast.type} variant="filled">
//           {toast.msg}
//         </Alert>
//       </Snackbar>
//     </Box>
//   );
// }


// import React, { useEffect, useState, useContext } from "react";
// import axios from "axios";
// import { Mail, Loader2, LogIn } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import { GoogleLogin } from "@react-oauth/google";




// /* 🔥 REUSABLE AUTH UI */


// import { AuthContext } from "./context/AuthContext";
// import AuthLayout from "./Auth/AuthLayout";
// import GlassCard from "./Auth/GlassCard";
// import AuthHeader from "./Auth/AuthHeader";
// import { BASE_URL } from "./config/api";


// export default function CustomerLogin() {
//   const navigate = useNavigate();
//   const { login } = useContext(AuthContext);

//   const [email, setEmail] = useState("");
//   const [otp, setOtp] = useState("");
//   const [step, setStep] = useState("email"); // email | otp
//   const [loading, setLoading] = useState(false);
//   const [cooldown, setCooldown] = useState(0);
//   const [error, setError] = useState("");

//   /* ---------------- OTP COOLDOWN TIMER ---------------- */
//   useEffect(() => {
//     if (cooldown <= 0) return;
//     const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
//     return () => clearInterval(timer);
//   }, [cooldown]);

//   /* ---------------- VALIDATION ---------------- */
//   const isValidEmail = (email) =>
//     /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email);

//   /* ---------------- SEND OTP ---------------- */
//   const sendOtp = async () => {
//     if (!isValidEmail(email)) {
//       setError("Enter a valid email address");
//       return;
//     }

//     try {
//       setLoading(true);
//       setError("");

//       const res = await axios.post(
//         `${BASE_URL}warranty/customer/send-email-otp`,
//         { email }
//       );

//       if (res.data?.status) {
//         setStep("otp");
//         setCooldown(120);
//       } else {
//         setError(res.data?.message || "Failed to send OTP");
//       }
//     } catch (err) {
//       setError(err?.response?.data?.message || "Failed to send OTP");
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ---------------- VERIFY OTP ---------------- */
//   const verifyOtp = async () => {
//     if (!otp || otp.length < 4) {
//       setError("Enter valid OTP");
//       return;
//     }

//     try {
//       setLoading(true);
//       setError("");

//       const res = await axios.post(
//         `${BASE_URL}warranty/customer/verify-email-otp`,
//         { email, otp }
//       );

//       if (res.data?.status) {
//         login({ ...res.data.data, role: 7 });
//         navigate("/custdashboard");
//       } else {
//         setError(res.data?.message || "Invalid OTP");
//       }
//     } catch (err) {
//       setError(err?.response?.data?.message || "OTP verification failed");
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ---------------- GOOGLE LOGIN ---------------- */
//   const handleGoogleLogin = async (credentialResponse) => {
//     try {
//       setLoading(true);
//       setError("");

//       const token = credentialResponse?.credential;
//       if (!token) {
//         setError("Google token missing");
//         return;
//       }

//       const res = await axios.post(
//         `${BASE_URL}warranty/customer/auth/google`,
//         { token }
//       );

//       if (res.data?.status) {
//         login({ ...res.data.data, role: 7 });
//         navigate("/custdashboard");
//       } else {
//         setError(res.data?.message || "Google login failed");
//       }
//     } catch (err) {
//       setError(err?.response?.data?.message || "Google login failed");
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ---------------- UI ---------------- */
//   return (
//     <AuthLayout showIllustration={true}>
//       <GlassCard>
//         <AuthHeader
//           title="Customer Login"
//           subtitle="Sign in to manage your warranties"
//         />

//         {/* EMAIL */}
//         <div className="mb-4">
//           <label className="text-sm font-medium text-slate-700">
//             Email Address
//           </label>

//           <div className="
//             relative rounded-xl bg-white/60 backdrop-blur border px-3
//             focus-within:ring-2 focus-within:ring-blue-400
//           ">
//             <Mail className="absolute left-3 top-3 text-slate-400" size={16} />

//             <input
//               value={email}
//               disabled={step === "otp"}
//               onChange={(e) => setEmail(e.target.value)}
//               placeholder="you@example.com"
//               className="w-full bg-transparent pl-6 py-2 text-sm outline-none disabled:opacity-60"
//             />
//           </div>
//         </div>

//         {/* OTP */}
//         {step === "otp" && (
//           <div className="mb-4">
//             <label className="text-sm font-medium text-slate-700">
//               Enter OTP
//             </label>

//             <input
//               value={otp}
//               onChange={(e) =>
//                 setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
//               }
//               placeholder="6 digit OTP"
//               className="
//                 w-full rounded-xl bg-white/60 backdrop-blur
//                 border px-3 py-2 text-sm outline-none
//                 focus:ring-2 focus:ring-blue-400
//               "
//             />
//           </div>
//         )}

//         {/* ERROR */}
//         {error && (
//           <p className="text-xs text-red-500 mb-3 text-center">
//             {error}
//           </p>
//         )}

//         {/* ACTION BUTTON */}
//         {step === "email" ? (
//           <button
//             onClick={sendOtp}
//             disabled={loading || cooldown > 0}
//             className="
//               w-full rounded-xl py-2 font-semibold text-white
//               bg-gradient-to-r from-blue-600 to-indigo-600
//               hover:from-blue-700 hover:to-indigo-700
//               transition disabled:opacity-60
//             "
//           >
//             {loading
//               ? "Sending OTP…"
//               : cooldown > 0
//               ? `Resend OTP in ${cooldown}s`
//               : "Send OTP"}
//           </button>
//         ) : (
//           <button
//             onClick={verifyOtp}
//             disabled={loading}
//             className="
//               w-full rounded-xl py-2 font-semibold text-white
//               bg-gradient-to-r from-blue-600 to-indigo-600
//               hover:from-blue-700 hover:to-indigo-700
//               transition disabled:opacity-60
//             "
//           >
//             {loading ? "Verifying…" : "Verify & Login"}
//           </button>
//         )}

//         {/* DIVIDER */}
//         {step === "email" && (
//           <div className="flex items-center gap-3 my-5">
//             <div className="flex-1 h-px bg-slate-200" />
//             <span className="text-xs text-slate-400">OR</span>
//             <div className="flex-1 h-px bg-slate-200" />
//           </div>
//         )}

//         {/* GOOGLE LOGIN */}
//         {step === "email" && (
//           <div className="flex justify-center">
//             <GoogleLogin
//               onSuccess={handleGoogleLogin}
//               onError={() => setError("Google login failed")}
//               shape="pill"
//             />
//           </div>
//         )}

//         <p className="mt-6 text-center text-xs text-slate-500">
//           © {new Date().getFullYear()} Goelectronix
//         </p>
//       </GlassCard>
//     </AuthLayout>
//   );
// }